import { FC, useEffect, useMemo, useState } from 'react';
import { Employee, fetchEmployee } from '../../api/employees';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

interface EmployeesDetailPanelProps {
  id: string;
  onBack: () => void;
}

const EmployeesDetailPanel: FC<EmployeesDetailPanelProps> = ({ id, onBack }) => {
  const [data, setData] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchEmployee(id)
      .then((response) => {
        if (isMounted) {
          setData(response);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load employee.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  const name = useMemo(() => {
    if (!data) {
      return '';
    }

    const display = `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim();
    return display.length ? display : data.username;
  }, [data]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white px-6 py-5 text-sm text-slate-500 shadow-inner">
        Loading employee...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-2xl bg-white px-6 py-5 text-sm text-danger shadow-inner">
        {error ?? 'Employee not found.'}
        <div className="mt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm text-white shadow"
            onClick={onBack}
          >
            Back to employees
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
    
      <PageHeaderWithBack
        title={name}
        description={<p className="mt-1 text-sm text-slate-500">Username {data.username}</p>}
        onBack={onBack}
        backLabel="Back to employees"
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Profile</h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP ID</dt>
              <dd>{data.erpId}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Employee ID</dt>
              <dd>{data.employeeId ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Staff ID</dt>
              <dd>{data.staffId ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Role</dt>
              <dd>{data.typeKey ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Locations</dt>
              <dd>
                {Array.isArray(data.locations)
                  ? data.locations.length === 0
                    ? 'All locations'
                    : data.locations.map((l) => l.name).join(', ')
                  : 'All locations'}
              </dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Created</dt>
              <dd>{new Date(data.createdAt).toLocaleString()}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last Updated</dt>
              <dd>{new Date(data.updatedAt).toLocaleString()}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Contact</h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email</dt>
              <dd>{data.email ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Mobile</dt>
              <dd>{data.mobileNumber ?? '—'}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Home Contact</dt>
              <dd>{data.homeContact ?? '—'}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default EmployeesDetailPanel;
